import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  DashboardBlock,
  DashboardBlocksMapping,
  HiddenDashboardBlockKey,
  User,
  UserDto,
} from "src/app/modules/auth/models/User";
import {UserService} from "../../../shared/services/user.service";
import {Observable, Subject, Subscription, debounceTime} from "rxjs";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {animate, style, transition, trigger} from "@angular/animations";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {MatSelectChange, MatSelect, MatOption} from "@angular/material/select";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {PushNotificationsService} from "src/app/services/pushNotifications.service";
import {PushPermissionResponse} from "src/app/interfaces/pushPermission.interface";
import {ProfileVisibility, ProfileVisibilityLabel} from "src/app/enums/User";
import {CountrySelectComponent} from "src/app/modules/shared/components/country-select/country-select.component";
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from "@angular/material/slide-toggle";
import {FormsModule} from "@angular/forms";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {NgClass, AsyncPipe} from "@angular/common";
import {MatRadioButton} from "@angular/material/radio";
import {MatCheckbox} from "@angular/material/checkbox";
import {EditRecommendationsPreferencesComponent} from "../edit-recommendations-preferences/edit-recommendations-preferences.component";
import {PlatformConsolePickerComponent} from "src/app/modules/shared/components/platform-console-picker/platform-console-picker.component";
import {MediaTypesGridComponent} from "src/app/modules/shared/components/media-types-grid/media-types-grid.component";
import {ConsumedMediaType} from "src/app/enums/ConsumedMediaType.enum";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {OnboardingService} from "src/app/modules/onboarding/services/onboarding.service";
import {COMPASS_PROFILE_PLATFORM_PRIMARY_KEYS} from "src/app/modules/compass/constants/compass-platform-console-options.constants";
import {CompassPlatformConsoleKey} from "src/app/modules/compass/enums/compass-recommendation-filters.enum";
import {parseCompassPlatformConsoleKeys} from "src/app/modules/compass/utils/compass-platform-console-selection.util";
import {PlatformService} from "src/app/services/platform.service";
import {RouterLink} from "@angular/router";

@Component({
  selector: "app-edit-profile-preferences",
  templateUrl: "./edit-profile-preferences.component.html",
  styleUrls: ["./edit-profile-preferences.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [
        style({opacity: 0}),
        animate("0.05s", style({opacity: 0})),
        animate("0.1s", style({opacity: 1})),
      ]),
      transition(":leave", [animate("0.1s", style({opacity: 0}))]),
    ]),
  ],
  imports: [
    MatSlideToggle,
    FormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    CountrySelectComponent,
    NgClass,
    MatRadioButton,
    MatCheckbox,
    AsyncPipe,
    TranslateModule,
    EditRecommendationsPreferencesComponent,
    PlatformConsolePickerComponent,
    MediaTypesGridComponent,
    RouterLink,
  ],
})
export class EditProfilePreferencesComponent implements OnInit, OnDestroy {
  @Input() user!: User;
  @Input() hidden = false;
  public isDarkTheme = false;
  public isPushEnabled = false;
  public showExternalRatings = true;
  public error?: CustomError | null;
  private readonly subscriptions: Subscription[] = [];
  public dashboardBlocks: DashboardBlock[] = Object.keys(
    DashboardBlocksMapping
  ).map((key: string) => ({
    key: key as HiddenDashboardBlockKey,
    label: DashboardBlocksMapping[key as HiddenDashboardBlockKey],
  }));
  public profileVisibilities: ProfileVisibility[] =
    Object.values(ProfileVisibility);
  public ProfileVisibilityLabel = ProfileVisibilityLabel;
  private readonly updatePreferencesSubject = new Subject<UserDto>();
  private readonly updatePreferencesSubscription: Subscription;
  public isCountrySaving = false;
  public readonly profilePlatformPrimaryKeys =
    COMPASS_PROFILE_PLATFORM_PRIMARY_KEYS;
  public platformConsoleKeys: CompassPlatformConsoleKey[] = [];
  public consumedTypes: ConsumedMediaType[] = [];
  private readonly consumedTypesSubject = new Subject<ConsumedMediaType[]>();

  constructor(
    private readonly translate: TranslateService,
    private readonly userService: UserService,
    private readonly toastService: ToastService,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly authService: AuthService,
    private readonly onboardingService: OnboardingService,
    private readonly platformService: PlatformService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.subscriptions.push(
      this.consumedTypesSubject
        .pipe(debounceTime(800))
        .subscribe((types) => this.persistConsumedTypes(types))
    );
    this.updatePreferencesSubscription = this.updatePreferencesSubject
      .pipe(debounceTime(1000))
      .subscribe({
        next: (updatedPreferences: UserDto) => {
          const updateUserSubscription = this.userService
            .updateUser(this.user._id, updatedPreferences)
            .subscribe({
              next: () => {
                this.error = null;
                this.toastService.sendToast({
                  text: "PREFERENCES_UPDATED",
                  classes: "bg-form-success",
                });
              },
              error: (error: CustomError) => {
                this.error = error;
                this.toastService.sendToast({
                  text: this.error.error.error,
                  classes: "bg-form-error",
                });
              },
            });
          this.subscriptions.push(updateUserSubscription);
        },
      });
    this.subscriptions.push(this.updatePreferencesSubscription);
  }

  ngOnInit() {
    this.isDarkTheme = this.user.theme === "dark";
    this.showExternalRatings =
      this.user.preferences.showExternalRatings !== false;
    this.consumedTypes = this.user.consumedTypes?.length
      ? [...this.user.consumedTypes]
      : Object.values(ConsumedMediaType);
    this.platformConsoleKeys = parseCompassPlatformConsoleKeys(
      this.user.preferences.platformConsoleKeys
    );

    const checkUserFcmTokensSubscription = this.pushNotificationsService
      .hasStoredFcmTokens(this.user._id)
      .subscribe({
        next: (hasToken) => {
          if (hasToken) {
            this.isPushEnabled = true;
          } else {
            this.isPushEnabled = false;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.isPushEnabled = false;
        },
      });
    this.subscriptions.push(checkUserFcmTokensSubscription);
  }

  toggleTheme() {
    this.user.theme = this.user.theme === "light" ? "dark" : "light";
    const root = document.documentElement;
    root.className = `${this.user.theme}`;
    this.platformService.syncNativeStatusBar(this.user.theme);

    const userInformationsForm: UserDto = {
      theme: this.user.theme,
    };

    const updateUserSubscription = this.userService
      .updateUser(this.user._id, userInformationsForm)
      .subscribe({
        next: () => {
          this.error = null;
          this.toastService.sendToast({
            text: "THEME_UPDATED",
            classes: "bg-form-success",
          });
        },
        error: (error: CustomError) => {
          this.error = error;
          this.user.theme = this.user.theme === "light" ? "dark" : "light";
          root.className = `${this.user.theme}`;
          this.platformService.syncNativeStatusBar(this.user.theme);
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(updateUserSubscription);
  }

  public toggleExternalRatings(event: MatSlideToggleChange): void {
    const previousValue = this.user.preferences.showExternalRatings !== false;
    this.showExternalRatings = event.checked;
    this.user.preferences.showExternalRatings = event.checked;

    const updateSubscription = this.userService
      .updateUser(this.user._id, {
        preferences: {...this.user.preferences},
      })
      .subscribe({
        next: (updatedUser) => {
          this.authService.user = updatedUser;
          this.error = null;
          this.toastService.sendToast({
            text: "PREFERENCES_UPDATED",
            classes: "bg-form-success",
          });
          this.cdr.markForCheck();
        },
        error: (error: CustomError) => {
          this.error = error;
          this.showExternalRatings = previousValue;
          this.user.preferences.showExternalRatings = previousValue;
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(updateSubscription);
  }

  togglePush(event: MatSlideToggleChange): void {
    const shouldEnable = event.checked;

    if (shouldEnable) {
      const updatePushSubscription = this.pushNotificationsService
        .requestPermission(this.user._id)
        .subscribe({
          next: (response: PushPermissionResponse) => {
            this.isPushEnabled = response.success || response.status === 409;

            if (response.success) {
              this.toastService.sendToast({
                text: "PUSH_NOTIFICATIONS_ENABLED",
                classes: "bg-form-success",
              });
            } else if (response.status === 409) {
              this.toastService.sendToast({
                text: "PUSH_NOTIFICATIONS_ALREADY_SUBSCRIBED",
                classes: "bg-form-success",
              });
            } else if (response.reason === "blocked") {
              this.toastService.sendToast({
                text: "PUSH_NOTIFICATIONS_BLOCKED",
                classes: "bg-form-error",
              });
            } else {
              this.toastService.sendToast({
                text: "PUSH_NOTIFICATIONS_FAILED",
                classes: "bg-form-error",
              });
            }
            this.cdr.markForCheck();
          },
          error: () => {
            this.isPushEnabled = false;
            this.toastService.sendToast({
              text: "PUSH_NOTIFICATIONS_FAILED",
              classes: "bg-form-error",
            });
            this.cdr.markForCheck();
          },
        });
      this.subscriptions.push(updatePushSubscription);
    } else {
      const updatePushSubscription = this.pushNotificationsService
        .unsubscribeFromPushNotifications(this.user._id)
        .subscribe({
          next: (success) => {
            this.isPushEnabled = !success;
            if (success) {
              this.toastService.sendToast({
                text: "PUSH_NOTIFICATIONS_DISABLED",
                classes: "bg-form-success",
              });
            } else {
              this.toastService.sendToast({
                text: "PUSH_NOTIFICATIONS_FAILED",
                classes: "bg-form-error",
              });
            }
            this.cdr.markForCheck();
          },
          error: () => {
            this.isPushEnabled = true;
            this.toastService.sendToast({
              text: "PUSH_NOTIFICATIONS_FAILED",
              classes: "bg-form-error",
            });
            this.cdr.markForCheck();
          },
        });
      this.subscriptions.push(updatePushSubscription);
    }
  }

  onLanguageChange(event: MatSelectChange) {
    this.user.language = event.value;

    const userLanguageForm: UserDto = {
      language: this.user.language,
    };

    const updateUserSubscription = this.userService
      .updateUser(this.user._id, userLanguageForm)
      .subscribe({
        next: () => {
          this.translate.use(this.user.language);
          this.error = null;
          this.toastService.sendToast({
            text: "PREFERENCES_UPDATED",
            classes: "bg-form-success",
          });
        },
        error: (error: CustomError) => {
          this.error = error;
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(updateUserSubscription);
  }

  onCountryChange(country: string): void {
    if (!country || country === this.user.country || this.isCountrySaving) {
      return;
    }

    const previousCountry = this.user.country;
    this.user.country = country;
    this.isCountrySaving = true;

    const updateUserSubscription = this.userService
      .updateUser(this.user._id, {country})
      .subscribe({
        next: () => {
          this.isCountrySaving = false;
          this.error = null;
          this.toastService.sendToast({
            text: "PREFERENCES_UPDATED",
            classes: "bg-form-success",
          });
          this.cdr.markForCheck();
        },
        error: (error: CustomError) => {
          this.isCountrySaving = false;
          this.user.country = previousCountry;
          this.error = error;
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(updateUserSubscription);
  }

  updateProfileVisibility(visibility: ProfileVisibility) {
    this.user.visibility = visibility;

    const userVisibilityForm: UserDto = {
      visibility: visibility,
    };

    const updateUserSubscription = this.userService
      .updateUser(this.user._id, userVisibilityForm)
      .subscribe({
        next: () => {
          this.error = null;
          this.toastService.sendToast({
            text: "PREFERENCES_UPDATED",
            classes: "bg-form-success",
          });
        },
        error: (error: CustomError) => {
          this.error = error;
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(updateUserSubscription);
  }

  isSelected(blockKey: HiddenDashboardBlockKey): boolean {
    return this.user.preferences.hiddenDashboardBlocks?.includes(blockKey);
  }

  hideDashboardBlock(blockKey: HiddenDashboardBlockKey): void {
    let newHiddenDashboardBlocks: HiddenDashboardBlockKey[];

    if (this.user.preferences.hiddenDashboardBlocks) {
      if (this.isSelected(blockKey)) {
        newHiddenDashboardBlocks =
          this.user.preferences.hiddenDashboardBlocks.filter(
            (key: HiddenDashboardBlockKey): boolean => key !== blockKey
          );
      } else {
        newHiddenDashboardBlocks = [
          ...this.user.preferences.hiddenDashboardBlocks,
          blockKey,
        ];
      }
    } else {
      newHiddenDashboardBlocks = [blockKey];
    }

    this.user.preferences.hiddenDashboardBlocks = newHiddenDashboardBlocks;

    const updatedPreferences: UserDto = {
      preferences: {...this.user.preferences},
    };

    this.updatePreferencesSubject.next(updatedPreferences);
  }

  public onPlatformConsoleKeysChange(keys: CompassPlatformConsoleKey[]): void {
    this.platformConsoleKeys = parseCompassPlatformConsoleKeys(keys);
    this.user.preferences.platformConsoleKeys = [...this.platformConsoleKeys];

    const updatedPreferences: UserDto = {
      preferences: {...this.user.preferences},
    };

    this.updatePreferencesSubject.next(updatedPreferences);
  }

  public onConsumedTypesChange(types: ConsumedMediaType[]): void {
    this.consumedTypes = types;
    this.user.consumedTypes = types;
    // Re-emit the user so the media-gating preferences refresh app-wide.
    this.authService.user = this.user;
    this.consumedTypesSubject.next(types);
  }

  private persistConsumedTypes(types: ConsumedMediaType[]): void {
    const subscription = this.onboardingService
      .updateConsumedTypes(types)
      .subscribe({
        next: () => {
          this.error = null;
          this.toastService.sendToast({
            text: "PREFERENCES_UPDATED",
            classes: "bg-form-success",
          });
        },
        error: (error: CustomError) => {
          this.error = error;
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(subscription);
  }

  public getProfileVisibilityLabel$(key: string): Observable<string> {
    return this.translate.get(
      ProfileVisibilityLabel[key as keyof typeof ProfileVisibilityLabel] ?? key
    );
  }

  trackByBlock(_index: number, item: DashboardBlock) {
    return item.key;
  }

  trackByValue(_index: number, value: string) {
    return value;
  }

  onKeyUp(
    event: KeyboardEvent,
    type: string,
    blockKey?: HiddenDashboardBlockKey,
    visibility?: ProfileVisibility
  ): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "hideDashboardBlock" && blockKey) {
        this.hideDashboardBlock(blockKey);
      } else if (type === "updateProfileVisibility" && visibility) {
        this.updateProfileVisibility(visibility);
      }
    }
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
